import ApiService from "@/core/services/ApiService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";

import { auth, db } from "@/core/services/FirebaseService";
import {
  setDoc,
  doc,
  writeBatch,
  query,
  collection,
  limit,
  orderBy,
  where,
  onSnapshot,
  startAfter,
  Timestamp,
  GeoPoint,
  getDocs,
  Query,
  DocumentData,
} from "firebase/firestore";
import { Unsubscribe } from "@firebase/util";

enum TransactionPageQuery {
  all = "all",
}

export enum TransactionStatus {
  cancelled = "cancelled",
  rejected = "rejected",
  finished = "finished",
  delivering = "delivering",
  preparing = "preparing",
  waiting = "waiting",
}

function getStatus(transaction: DocumentData): TransactionStatus {
  if (transaction?.date?.cancelled) {
    return TransactionStatus.cancelled;
  } else if (transaction?.date?.rejected) {
    return TransactionStatus.rejected;
  } else if (transaction?.date?.finished) {
    return TransactionStatus.finished;
  } else if (transaction?.date?.delivery) {
    return TransactionStatus.delivering;
  } else if (transaction?.date?.verified) {
    return TransactionStatus.preparing;
  } else {
    return TransactionStatus.waiting;
  }
}

interface TransactionStoreState {
  doc: { [key: string]: TransactionDoc };
  pageQuery: { [key in TransactionPageQuery]: Array<string> };
  textQuery: { [key: string]: Array<string> };
}

interface TransactionDoc {
  id: string;
  data: TransactionDocData;
  unsub: Unsubscribe | null;
}

interface TransactionDocData {
  tid: string;
  uid: string;
  versionNo: number;
  status: TransactionStatus;

  note: string;
  date: {
    created: Timestamp | null;
    verified: Timestamp | null;
    delivery: Timestamp | null;
    finished: Timestamp | null;
    rejected: Timestamp | null;
    cancelled: Timestamp | null;
  };
  payment: {
    type: string;
    price: number;
    receiptImageURL: string;
  };
  delivery: {
    courier: string;
    receiptImageURL: string;
    receiptID: string;
    geopoint: GeoPoint;
    name: string;
    address: string;
  };
  item: {
    [key: string]: {
      name: string;
      qty: number;
      price: number;
    };
  };
}

@Module
export default class TransactionModule
  extends VuexModule
  implements TransactionStoreState
{
  errorMessage = null;
  doc = {} as { [key: string]: TransactionDoc };
  pageQuery = {} as { [key in TransactionPageQuery]: Array<string> };
  textQuery = {} as { [key: string]: Array<string> };

  @Mutation
  [Mutations.TRANSACTION_SET_DOC](payload: {
    id: string;
    data: DocumentData;
    unsub: Unsubscribe | undefined;
  }) {
    const id = payload.id;
    const data = payload.data;
    const unsub = payload.unsub;

    const docData = {
      tid: id,
      uid: data.uid ?? "",
      versionNo: data.versionNo ?? 0,
      status: getStatus(data),

      note: data.note,
      date: {
        created: data.date?.created ?? null,
        verified: data.date?.verified ?? null,
        delivery: data.date?.delivery ?? null,
        finished: data.date?.finished ?? null,
        rejected: data.date?.rejected ?? null,
        cancelled: data.date?.cancelled ?? null,
      },
      payment: {
        type: data.payment?.type ?? "",
        price: data.payment?.price ?? 0,
        receiptImageURL: data.payment?.receiptImageURL ?? "",
      },
      delivery: {
        courier: data.delivery?.courier ?? "",
        receiptImageURL: data.delivery?.receiptImageURL ?? "",
        receiptID: data.delivery?.receiptID ?? "",
        geopoint: data.delivery?.geopoint ?? null,
        name: data.delivery?.name ?? "",
        address: data.delivery?.address ?? "",
      },
      item: Object.entries<DocumentData>(data.item ?? data.items ?? {}).reduce((res, cV) => {
        return {
          ...res,
          [cV[0]]: {
            name: cV[1]?.name ?? "",
            qty: cV[1]?.qty ?? 0,
            price: cV[1]?.price ?? 0,
          },
        };
      }, {}),
    } as TransactionDocData;

    if (unsub) {
      this.doc[id] = {
        id: id,
        data: docData,
        unsub: unsub,
      };
    } else if (this.doc[id]) {
      this.doc[id].data = docData;
    } else {
      console.error(`FAILED SETTING TRANSACTION DOC`);
    }
  }

  @Mutation
  [Mutations.TRANSACTION_SET_PAGE_QUERY](payload: {
    key: TransactionPageQuery;
    ids: Array<string>;
    append: boolean;
  }) {
    const key = payload.key;
    const ids = payload.ids;
    const append = payload.append;

    if (append && this.pageQuery[key]) {
      this.pageQuery[key] = [...this.pageQuery[key], ...ids];
    } else {
      this.pageQuery[key] = ids;
    }
  }

  @Mutation
  [Mutations.TRANSACTION_SET_TEXT_QUERY](payload: {
    key: string;
    ids: Array<string>;
    append: boolean;
  }) {
    const key = payload.key;
    const ids = payload.ids;
    const append = payload.append;

    if (append && this.textQuery[key]) {
      this.textQuery[key] = [...this.textQuery[key], ...ids];
    } else {
      this.textQuery[key] = ids;
    }
  }

  @Action
  [Actions.TRANSACTION_POPULATE_PAGE_QUERY](payload: {
    key: TransactionPageQuery;
    append: boolean;
  }) {
    const key = payload.key;
    const append = payload.append;

    let dbQuery: Query<DocumentData>;
    switch (key) {
      case TransactionPageQuery.all:
      default:
        dbQuery = query(
          collection(db, "transactions"),
          where("versionNo", "==", 0),
          orderBy("date.created", "desc"),
          // limit(50)
        );
        break;
    }

    getDocs(dbQuery)
      .then((dbDocs) => {
        console.log(
          "done",
          dbDocs.docs.map((e) => e.id)
        );
        const ids: Array<string> = [];

        dbDocs.forEach((dbDoc) => {
          ids.push(dbDoc.id);
          if (!this.doc[dbDoc.id]) {
            const unsub = onSnapshot(
              doc(db, "transactions", dbDoc.id),
              (dbDoc) => {
                this.context.commit(Mutations.TRANSACTION_SET_DOC, {
                  id: dbDoc.id,
                  data: dbDoc.data(),
                });
              }
            );
            this.context.commit(Mutations.TRANSACTION_SET_DOC, {
              id: dbDoc.id,
              data: dbDoc.data(),
              unsub: unsub,
            });
          }
        });

        this.context.commit(Mutations.TRANSACTION_SET_PAGE_QUERY, {
          key: key,
          ids: ids,
          append: append,
        });
      })
      .catch((error) => console.log("owo", error.message));
  }

  @Action
  async [Actions.TRANSACTION_POPULATE_TEXT_QUERY](payload: {
    key: string;
    append: boolean;
  }) {
    const key = payload.key;
    const append = payload.append;

    const dbQuery = query(
      collection(db, "transactions"),
      where("versionNo", "==", 0),
      orderBy("farmerName"),
      where("farmerName", "==", key),
      where("farmerName", "==", key + "~"),
      // limit(50)
    );

    const dbDocs = await getDocs(dbQuery);
    const ids: Array<string> = [];

    dbDocs.forEach((dbDoc) => {
      ids.push(dbDoc.id);
      if (!this.doc[dbDoc.id]) {
        this.context.commit(Mutations.TRANSACTION_SET_DOC, {
          id: dbDoc.id,
          data: dbDoc.data(),
          unsub: onSnapshot(doc(db, "transactions", dbDoc.id), (dbDoc) => {
            this.context.commit(Mutations.TRANSACTION_SET_DOC, {
              id: dbDoc.id,
              data: dbDoc.data(),
            });
          }),
        });
      }
    });

    this.context.commit(Mutations.TRANSACTION_SET_TEXT_QUERY, {
      key: key,
      ids: ids,
      append: append,
    });
  }

  @Action({ rawError: true })
  [Actions.TRANSACTION_UPDATE_DOC](payload: {
    id: string;
    transaction: { [x: string]: any };
  }) {
    console.log("RBW_UPDATE_DOC");
    const id = payload.id;
    const transaction = payload.transaction;

    // Get a new write batch
    const batch = writeBatch(db);

    // Set profile value
    const transactionRef = doc(db, "transactions", id);
    batch.update(transactionRef, transaction);

    console.log(id, transaction);

    // Commit the batch
    return batch.commit();
  }

  @Action
  async [Actions.TRANSACTION_CREATE](payload: {
    id: string;
    transaction: DocumentData;
  }) {
    const id = payload.id;
    const transaction = payload.transaction;

    // Get a new write batch
    const batch = writeBatch(db);

    if (transaction) {
      // Set profile value
      const transactionRef = doc(db, "transactions", id);
      batch.set(transactionRef, {
        versionNo: 0,
        ...transaction,
      });
    }

    // Commit the batch
    return batch.commit();
  }
}

export {
  TransactionPageQuery,
  TransactionStoreState,
  TransactionDoc,
  TransactionDocData,
};
